import React, { useState } from "react";
import { Card, Progress, Space, Tag, Typography } from "antd";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripMarkdown } from "../cardUtils";
import { CustomTooltip } from "src/components/CustomTooltip";
import { Tags } from ".";

/**
 * ThemeCard Component
 *
 * A card component for displaying theme information with activity metrics, status, and related keywords.
 * This component is designed to show thematic groupings and their associated metrics.
 *
 * @example
 * ```tsx
 * <ThemeCard
 *   theme="Security Risk Management"
 *   description="Increased focus on security protocols and risk assessment"
 *   activityCount={42}
 *   activityUnit="comments"
 *   change="+18% vs prior week"
 *   status="increasing"
 *   keywords={["security", "risk", "compliance"]}
 * />
 * ```
 */
export interface ThemeItemProps {
  /**
   * The theme name or category
   */
  theme: string;

  /**
   * Description of why the theme matters
   */
  description: string;

  /**
   * Number of activities in this theme
   */
  activityCount?: number;

  /**
   * Unit of measurement for activities (e.g., 'comments', 'contributors', 'issues')
   */
  activityUnit?: string;

  /**
   * Number of contributors in this theme
   */
  contributorCount?: number;

  /**
   * Percentage of contributionss vs other work
   */
  contributionPercentage?: number;

  /**
   * Change in activity (e.g., '+18% vs prior week')
   */
  change?: string;

  /**
   * Evidence for the theme
   */
  evidence?: string[];

  /**
   * Current status of the theme
   */
  status?: "increasing" | "flat" | "declining" | "resurfacing";

  /**
   * Array of related keywords or tags
   */
  keywords?: string[];

  /**
   * Optional CSS class name for custom styling
   */
  className?: string;

  /**
   * Optional icon to display with the theme
   */
  icon?: React.ReactNode;

  /**
   * Layout variant for the card: 'default' (vertical), 'pill' (compact horizontal), 'minimal' (text-only), 'rich', 'compact', 'list', 'expandable'
   */
  variant?:
    | "default"
    | "pill"
    | "minimal"
    | "full"
    | "compact"
    | "list"
    | "expandable";

  /**
   * Optional progress stroke color configuration
   */
  progressStrokeColor?: {
    from: string;
    to: string;
    direction: string;
  };
}

export const ThemeItem: React.FC<ThemeItemProps> = ({
  theme,
  description,
  activityCount,
  activityUnit,
  contributorCount,
  contributionPercentage,
  change,
  status,
  keywords,
  evidence,
  className,
  icon,
  variant = "default",
  progressStrokeColor,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  if (variant === "pill") {
    // Compact horizontal pill card
    return (
      <div className={[className, "flex-col-full"].filter(Boolean).join(" ")}>
        <div style={{ display: "flex", flexDirection: "row", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontWeight: 600 }}>{theme}</div>
          </div>
          <Space size={4} wrap>
            <Tag className="vzbl-pill-xsm">{status}</Tag>
            <Tag className="vzbl-pill-xsm">
              {activityCount} {activityUnit}
            </Tag>
            <Tag className="vzbl-pill-xsm">{change}</Tag>
            {keywords && keywords.length > 0 && (
              <>
                {keywords.map((keyword, idx) => (
                  <Tag key={idx} className="vzbl-pill-xsm">
                    {stripMarkdown(keyword)}
                  </Tag>
                ))}
              </>
            )}
          </Space>
        </div>
      </div>
    );
  }
  if (variant === "minimal") {
    // Minimal, text-only card
    return (
      <div
        className={[className, "flex-col-full", "vzbl-text-3"]
          .filter(Boolean)
          .join(" ")}
        style={{ padding: 8 }}
      >
        <strong>{stripMarkdown(theme)}</strong>:{" "}
        <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
          {description}
        </Markdown>
      </div>
    );
  }
  // Rich Card Variant
  if (variant === "full") {
    return (
      <div className={[className, "flex-col-full"].filter(Boolean).join(" ")}>
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <Typography.Text className="vzbl-header-4 vzbl-clamp-1-line">
                {stripMarkdown(theme)}
              </Typography.Text>
            </div>
            <Typography.Text
              className="vzbl-text-3"
              style={{ fontStyle: "italic" }}
            >
              <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
                {description}
              </Markdown>
            </Typography.Text>
            {evidence &&
              evidence.length > 0 &&
              evidence.slice(0, 3).map((evidenceItem, idx) => (
                <span
                  key={idx}
                  className="vzbl-text-3"
                  style={{ paddingLeft: "var(--padding-3)" }}
                >
                  <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
                    {evidenceItem}
                  </Markdown>
                </span>
              ))}
          </div>
          {/* Stats row at the bottom */}
          <div
            style={{
              display: "flex",
              gap: 16,
              justifyContent: "flex-start",
              marginTop: "auto",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <span className="vzbl-header-3">{activityCount}</span>
              <span
                className="vzbl-text-3"
                style={{ paddingLeft: "var(--padding-3)" }}
              >
                {activityUnit}
              </span>
            </div>
            <div style={{ textAlign: "center" }}>
              <span className="vzbl-header-3">{contributorCount}</span>
              <span
                className="vzbl-text-3"
                style={{ paddingLeft: "var(--padding-3)" }}
              >
                Contributors
              </span>
            </div>
          </div>
          {contributionPercentage && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Progress
                percent={Math.round(contributionPercentage)}
                strokeColor={
                  progressStrokeColor || "var(--color-pill-purple-accent)"
                }
              />
            </div>
          )}
          {keywords && keywords.length > 0 && (
            <Tags tags={keywords} tagClassName="vzbl-pill-xsm vzbl-pill-tag" />
          )}
        </div>
      </div>
    );
  }
  // Compact Stat Card Variant
  if (variant === "compact") {
    return (
      <div
        className={[className, "vzbl-theme-compact"].filter(Boolean).join(" ")}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "space-between",
              flexDirection: "row",
            }}
          >
            <div style={{ fontWeight: 600 }}>{theme}</div>
            <Tag className="vzbl-pill-xsm vzbl-pill-tag">{status}</Tag>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Tag className="vzbl-pill-xsm vzbl-pill-clear">
              {activityCount} {activityUnit}
            </Tag>
            <Tag className="vzbl-pill-xsm vzbl-pill-clear">{change}</Tag>
          </div>
        </div>
      </div>
    );
  }
  // List Item Variant
  if (variant === "list") {
    return (
      <div
        className={[className, "vzbl-theme-list"].filter(Boolean).join(" ")}
        style={{
          display: "flex",
          alignItems: "center",
          padding: 12,
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        {icon && <div style={{ fontSize: 24, marginRight: 12 }}>{icon}</div>}
        <div style={{ flex: 1 }}>
          <div className="vzbl-header-3">{theme}</div>
          <div className="vzbl-text-3">{description}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Tag className="vzbl-pill-xsm">{status}</Tag>
          <Tag className="vzbl-pill-xsm">
            {activityCount} {activityUnit}
          </Tag>
          <Tag className="vzbl-pill-xsm">{change}</Tag>
        </div>
      </div>
    );
  }
  // Expandable Card Variant
  if (variant === "expandable") {
    return (
      <div
        className={[className, "vzbl-theme-expandable", "vzbl-panel-shadow"]
          .filter(Boolean)
          .join(" ")}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => setExpanded((e) => !e)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {icon && <span style={{ fontSize: 20 }}>{icon}</span>}
            <Typography.Text className="vzbl-header-3 single-line">
              {theme}
            </Typography.Text>
            <Tag className="vzbl-pill-sm">{status}</Tag>
          </div>
          <span style={{ fontSize: 18, marginLeft: 8 }}>
            {expanded ? "▲" : "▼"}
          </span>
        </div>
        {expanded && (
          <div style={{ marginTop: 12 }}>
            <Typography.Text
              className="vzbl-text-3"
              style={{
                fontStyle: "italic",
                color: "var(--color-text-secondary)",
              }}
            >
              {description}
            </Typography.Text>
            <div style={{ display: "flex", gap: 16, margin: "12px 0" }}>
              <div style={{ textAlign: "center" }}>
                <div className="vzbl-header-3">{activityCount}</div>
                <div className="vzbl-text-3">{activityUnit}</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <Tag className="vzbl-pill-sm">{status}</Tag>
                <div className="vzbl-text-3">Status</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <Tag className="vzbl-pill-sm">{change}</Tag>
                <div className="vzbl-text-3">Change</div>
              </div>
            </div>
            {keywords && keywords.length > 0 && (
              <Space
                size={4}
                wrap
                style={{ flexWrap: "wrap", maxWidth: "100%" }}
              >
                {keywords.map((keyword, idx) => (
                  <Tag key={idx} className="vzbl-pill-sm">
                    {stripMarkdown(keyword)}
                  </Tag>
                ))}
              </Space>
            )}
          </div>
        )}
      </div>
    );
  }
  // Default vertical card
  return (
    <div
      className={[className, "vzbl-theme-default", "vzbl-panel-shadow"]
        .filter(Boolean)
        .join(" ")}
    >
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography.Text className="vzbl-header-4 single-line">
            <CustomTooltip content={stripMarkdown(theme)}>
              {stripMarkdown(theme)}
            </CustomTooltip>
          </Typography.Text>
          <Space size={8}>
            <Tag className="vzbl-pill-xsm vzbl-pill-clear">
              {activityCount} {activityUnit}
            </Tag>
          </Space>
        </div>

        <Typography.Text className="vzbl-text-3">
          <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
            {description}
          </Markdown>
        </Typography.Text>

        {keywords && keywords.length > 0 && (
          <Space size={4} wrap style={{ flexWrap: "wrap", maxWidth: "100%" }}>
            <Tags tags={keywords} tagClassName="vzbl-pill-xsm vzbl-pill-tag" />
          </Space>
        )}

        {contributionPercentage && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Progress
              percent={Math.round(contributionPercentage)}
              strokeColor={
                progressStrokeColor || "var(--color-pill-purple-accent)"
              }
            />
          </div>
        )}
      </Space>
    </div>
  );
};
